/**
 * DragTestPage - Minimal React drag test to isolate the issue
 *
 * This page tests the drag interaction WITHOUT:
 * - Three.js
 * - Complex state management
 * - Building editor store
 * - Multiple useEffect dependencies
 *
 * It uses a simple Mapbox marker to test drag behavior.
 * If this works but BuildingEditorMap doesn't, the issue is
 * in the complex state/effects of that component.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN
const CENTER: [number, number] = [-83.0458, 42.3314] // Detroit

interface LogEntry {
  time: string
  msg: string
  type: 'info' | 'start' | 'end' | 'warn' | 'error'
}

export function DragTestPage() {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)

  // Debug state - displayed in UI
  const [debug, setDebug] = useState({
    isDragging: false,
    dragCount: 0,
    effectRuns: 0,
    dragPanEnabled: true,
    hitDistance: '-',
    lastEvent: 'none',
  })
  const [logs, setLogs] = useState<LogEntry[]>([])

  // Refs for drag state (persist across renders)
  const isDraggingRef = useRef(false)
  const boxPosRef = useRef({ lng: CENTER[0], lat: CENTER[1] })
  const effectRunsRef = useRef(0)

  const log = useCallback((msg: string, type: LogEntry['type'] = 'info') => {
    const time = new Date().toISOString().substr(11, 12)
    console.log(`[DRAG-TEST] ${msg}`)
    setLogs((prev) => [{ time, msg, type }, ...prev.slice(0, 25)])
  }, [])

  // Initialize map ONCE - handle React Strict Mode
  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN) return

    // Check if map already exists and is still valid
    if (mapRef.current) {
      log('Map already exists, skipping init')
      return
    }

    log('Initializing map...')
    mapboxgl.accessToken = MAPBOX_TOKEN

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: CENTER,
      zoom: 16,
      pitch: 0,
      bearing: 0,
      antialias: true,
    })

    mapRef.current = map

    // Create draggable marker
    const el = document.createElement('div')
    el.style.cssText = `
      width: 120px; height: 80px;
      background: linear-gradient(135deg, #ff6b00 0%, #ff9500 100%);
      border: 3px solid white;
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      color: white; font-weight: bold; font-size: 14px;
      pointer-events: none;
      box-shadow: 0 4px 15px rgba(0,0,0,0.4);
      text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      transition: transform 0.1s, box-shadow 0.1s;
    `
    el.textContent = 'DRAG ME'
    el.id = 'drag-marker'

    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat(CENTER)
      .addTo(map)
    markerRef.current = marker

    map.on('load', () => {
      log('Map loaded - ready for testing')

      // CRITICAL WORKAROUND: Re-order event listeners by calling disable/enable
      // This fixes issue where dragPan.disable() doesn't work during mousedown
      // See: https://github.com/mapbox/mapbox-gl-js/issues/2419
      map.dragPan.disable()
      map.dragPan.enable()
      map.dragRotate.disable()
      map.dragRotate.enable()
      log('Reordered dragPan/dragRotate listeners')
    })

    // Don't cleanup map in dev (React Strict Mode causes double-run)
    // Only cleanup on actual unmount
    return () => {
      log('Map cleanup - keeping map for Strict Mode')
      // Note: In production, you would want to cleanup properly
      // map.remove()
      // mapRef.current = null
    }
  }, [log])

  // Track map loaded state
  const [mapLoaded, setMapLoaded] = useState(false)

  // Update loaded state when map fires 'load' event
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const handleLoad = () => {
      setMapLoaded(true)
    }

    // Check if already loaded
    if (map.loaded()) {
      setMapLoaded(true)
    } else {
      map.on('load', handleLoad)
    }

    return () => {
      map.off('load', handleLoad)
    }
  }, [])

  // Setup drag listeners - wait for map to be loaded
  // Using Mapbox's event system instead of canvas events
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    effectRunsRef.current++
    setDebug((prev) => ({ ...prev, effectRuns: effectRunsRef.current }))
    log(`Effect run #${effectRunsRef.current} - attaching Mapbox listeners`)

    const markerEl = document.getElementById('drag-marker')

    // Use Mapbox's event system - e.point gives screen coordinates
    const handleMouseDown = (e: mapboxgl.MapMouseEvent) => {
      setDebug((prev) => ({ ...prev, lastEvent: 'mousedown' }))

      // Screen-space hit detection using e.point
      const boxScreen = map.project([boxPosRef.current.lng, boxPosRef.current.lat])
      const dist = Math.hypot(e.point.x - boxScreen.x, e.point.y - boxScreen.y)

      setDebug((prev) => ({ ...prev, hitDistance: `${dist.toFixed(1)}px` }))

      const HIT_RADIUS = 100

      if (dist < HIT_RADIUS) {
        isDraggingRef.current = true

        // Prevent default map behavior
        e.preventDefault()

        map.dragPan.disable()

        if (markerEl) {
          markerEl.style.transform = 'scale(1.05)'
          markerEl.style.boxShadow = '0 8px 25px rgba(255,107,0,0.6)'
        }

        setDebug((prev) => ({
          ...prev,
          isDragging: true,
          dragCount: prev.dragCount + 1,
          dragPanEnabled: false,
        }))

        log(`DRAG STARTED (dist=${dist.toFixed(1)}px)`, 'start')
      } else {
        log(`Miss: ${dist.toFixed(1)}px > ${HIT_RADIUS}px`, 'info')
      }
    }

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (!isDraggingRef.current) return

      setDebug((prev) => ({ ...prev, lastEvent: 'mousemove' }))

      // Use e.lngLat directly from Mapbox event
      boxPosRef.current = { lng: e.lngLat.lng, lat: e.lngLat.lat }
      markerRef.current?.setLngLat([e.lngLat.lng, e.lngLat.lat])
    }

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return

      setDebug((prev) => ({ ...prev, lastEvent: 'mouseup' }))

      isDraggingRef.current = false
      map.dragPan.enable()

      const markerEl = document.getElementById('drag-marker')
      if (markerEl) {
        markerEl.style.transform = 'scale(1)'
        markerEl.style.boxShadow = '0 4px 15px rgba(0,0,0,0.4)'
      }

      setDebug((prev) => ({
        ...prev,
        isDragging: false,
        dragPanEnabled: true,
      }))

      log('DRAG ENDED', 'end')
    }

    // Use Mapbox event system
    map.on('mousedown', handleMouseDown)
    map.on('mousemove', handleMouseMove)
    map.on('mouseup', handleMouseUp)

    return () => {
      log('Cleanup - removing listeners', 'warn')

      // CRITICAL: Re-enable dragPan if drag was active during cleanup
      if (isDraggingRef.current) {
        log('Cleanup during ACTIVE drag - re-enabling dragPan', 'error')
        isDraggingRef.current = false
        map.dragPan.enable()
      }

      map.off('mousedown', handleMouseDown)
      map.off('mousemove', handleMouseMove)
      map.off('mouseup', handleMouseUp)
    }
  }, [log, mapLoaded]) // Depends on log and mapLoaded

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      <div ref={containerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }} />

      {/* Debug overlay */}
      <div
        className="fixed top-3 left-3 bg-black/90 text-green-400 p-4 font-mono text-xs z-[10000] rounded-lg min-w-[300px] shadow-lg"
        style={{ border: '1px solid #333' }}
      >
        <div className="text-white font-bold text-sm mb-3 pb-2 border-b border-gray-700">
          DRAG TEST (Minimal React)
        </div>

        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500">isDragging:</span>
            <span className={debug.isDragging ? 'text-green-400 font-bold' : 'text-gray-600'}>
              {String(debug.isDragging)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">dragPan:</span>
            <span className={debug.dragPanEnabled ? 'text-gray-500' : 'text-yellow-400 font-bold'}>
              {debug.dragPanEnabled ? 'enabled' : 'DISABLED'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Drag Count:</span>
            <span className="text-white font-bold">{debug.dragCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Effect Runs:</span>
            <span className={debug.effectRuns > 1 ? 'text-red-400 font-bold' : 'text-white'}>
              {debug.effectRuns}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Hit Distance:</span>
            <span>{debug.hitDistance}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Last Event:</span>
            <span>{debug.lastEvent}</span>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-700 text-[10px] max-h-[180px] overflow-y-auto">
          {logs.map((l, i) => (
            <div
              key={i}
              className={`py-0.5 ${
                l.type === 'start'
                  ? 'text-green-400'
                  : l.type === 'end'
                    ? 'text-cyan-400'
                    : l.type === 'warn'
                      ? 'text-yellow-400'
                      : l.type === 'error'
                        ? 'text-red-400'
                        : 'text-gray-400'
              }`}
              style={{ opacity: i === 0 ? 1 : 0.7 }}
            >
              <span className="text-gray-600">[{l.time}]</span> {l.msg}
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-black/80 text-white px-5 py-3 rounded-lg z-[10000] text-sm">
        Click and drag the orange box. Try dragging multiple times to test consistency.
      </div>
    </div>
  )
}
